import React, { useState, useEffect, useMemo } from 'react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { PayPalButtonsWrapper } from './checkout/PayPalButtonsWrapper';
import { OrderSuccessScreen } from './checkout/OrderSuccessScreen';
import { StepIndicator } from './checkout/StepIndicator';
import { CartItemsList } from './checkout/CartItemsList';
import { AddressForm } from './checkout/AddressForm';
import { OrderSummary } from './checkout/OrderSummary';
import { PaymentMethodSelector } from './checkout/PaymentMethodSelector';
import { TransferSection } from './checkout/TransferSection';
import { getCheckoutText, getVariantLabels, SHIPPING_FEE } from './checkout/checkoutText';
import { useLanguage } from '../context/LanguageContext';
import { cartUtils } from '../data/translatedData';
import { trackCheckoutStart } from '../lib/analytics';
import { buildWhatsAppOrderUrl } from '../utils/whatsappOrder';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { ShoppingCart, X, ArrowLeft } from 'lucide-react';
import { tx } from '@/translations/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serializeCartItems = (cartItems) => cartItems.map(item => ({
  id: item.id, name: item.name, price: item.price, quantity: item.quantity,
  size: item.size || item.selectedSize || 'N/A',
  color: item.selectedColor?.name || null,
  tradition: item.selectedTradition || null,
  threads: item.selectedHilos || null,
  cottonType: item.selectedCottonType || null,
}));

const Checkout = ({ onClose, onSuccess: onSuccessProp }) => {
  const { language } = useLanguage();
  const [cartItems, setCartItems] = useState([]);
  const [orderComplete, setOrderComplete] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);
  const [lastOrderTotal, setLastOrderTotal] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [transferSubmitted, setTransferSubmitted] = useState(false);
  const [transferProcessing, setTransferProcessing] = useState(false);
  const [addressValid, setAddressValid] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState(null);
  const [mpProcessing, setMpProcessing] = useState(false);
  const [address, setAddress] = useState({
    fullName: '', street: '', interior: '', city: '', state: '', zip: '', country: 'MX', phone: '', zone: '', zoneOther: '', email: '', deliverySpec: '', deliverySpecOther: ''
  });
  const [coupon, setCoupon] = useState(null); // { code, discount_mxn, type, value }
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const text = getCheckoutText(language);
  const labels = getVariantLabels(language);

  const paypalOptions = useMemo(() => ({
    clientId: paypalClientId,
    currency: 'MXN',
    intent: 'capture',
    components: 'buttons',
    locale: 'es_MX',
  }), [paypalClientId]);

  useEffect(() => { setCartItems(cartUtils.getCart()); trackCheckoutStart(); }, []);

  useEffect(() => {
    const { fullName, street, phone, zone, zoneOther, deliverySpec, deliverySpecOther } = address;
    const zoneValid = !!zone && (zone !== 'Otro' || !!zoneOther.trim());
    const deliveryValid = !!deliverySpec && (deliverySpec !== 'Otro' || !!deliverySpecOther.trim());
    setAddressValid(!!(fullName && street && phone && zoneValid && deliveryValid));
  }, [address]);

  // Fetch PayPal client ID once
  useEffect(() => {
    fetch(`${API_URL}/api/paypal/client-id`)
      .then(r => r.json())
      .then(d => setPaypalClientId(d.client_id))
      .catch(err => console.error('Failed to load PayPal client ID:', err));
  }, []);

  const getSubtotal = () => cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const getItemCount = () => cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const getShippingFee = () => (getItemCount() >= 5 ? 0 : SHIPPING_FEE);
  const getPreDiscountTotal = () => getSubtotal() + getShippingFee();
  const getDiscount = () => coupon?.discount_mxn || 0;
  const getTotal = () => Math.max(0, getPreDiscountTotal() - getDiscount());
  const handleAddressChange = (field, value) => setAddress(prev => ({ ...prev, [field]: value }));

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, subtotal: getSubtotal() }),
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        setCoupon(data);
        setCouponError('');
      } else {
        setCoupon(null);
        setCouponError(data.detail || tx(language, 'Código no válido', 'Invalid code', 'קוד לא תקין'));
      }
    } catch {
      setCouponError(tx(language, 'Error de conexión', 'Connection error', 'שגיאת חיבור'));
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const buildWhatsAppUrl = (items, addr, totalAmount, method, orderId = null) => {
    const url = buildWhatsAppOrderUrl(items, addr, totalAmount, method, orderId);
    setWhatsappUrl(url);
    return url;
  };

  const handlePaypalSuccess = (method) => {
    const orderId = Math.random().toString(36).slice(2, 10);
    setLastOrderTotal(getTotal());
    setLastOrderId(orderId);
    setOrderComplete(true);
    cartUtils.clearCart();
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(text.success);
    if (onSuccessProp) onSuccessProp();
    // PayPal onApprove is async so window.open may be blocked - URL saved for button fallback
    buildWhatsAppUrl(cartItems, address, getTotal(), method, orderId);
  };

  const handlePaypalError = (msg) => {
    toast.error(msg || text.error);
  };

  const handleMercadoPago = async () => {
    setMpProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/mercadopago/create-preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: serializeCartItems(cartItems),
          total: parseFloat(getPreDiscountTotal().toFixed(2)),
          shipping_address: address,
          return_url_base: window.location.origin,
          coupon_code: coupon?.code || null,
        })
      });
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      // Build WhatsApp URL before navigating away
      buildWhatsAppUrl(cartItems, address, getTotal(), 'Mercado Pago');
      window.location.href = data.init_point;
    } catch (err) {
      toast.error(text.error);
      setMpProcessing(false);
    }
  };

  const handleTransferSubmit = async () => {
    setTransferProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/transfer/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          total: parseFloat(getPreDiscountTotal().toFixed(2)),
          items: serializeCartItems(cartItems),
          shipping_address: address,
          coupon_code: coupon?.code || null,
        })
      });
      if (res.ok) {
        const orderData = await res.json().catch(() => ({}));
        const orderId = orderData.order_id || Math.random().toString(36).slice(2, 10);
        setLastOrderTotal(getTotal());
        setLastOrderId(orderId);
        setTransferSubmitted(true);
        cartUtils.clearCart();
        window.dispatchEvent(new Event('cartUpdated'));
        toast.success(text.transferSuccess);
        buildWhatsAppUrl(cartItems, address, getTotal(), 'Transferencia', orderId);
        if (onSuccessProp) onSuccessProp();
      } else {
        toast.error(text.error);
      }
    } catch (err) { toast.error(text.error); }
    finally { setTransferProcessing(false); }
  };

  if (orderComplete || transferSubmitted) {
    return (
      <OrderSuccessScreen
        transferSubmitted={transferSubmitted}
        lastOrderId={lastOrderId}
        paymentMethod={paymentMethod}
        language={language}
        cartItems={cartItems}
        lastOrderTotal={lastOrderTotal}
        whatsappUrl={whatsappUrl}
        onClose={onClose}
      />
    );
  }

  let currentStep = 0;
  if (paymentMethod) currentStep = 2;
  else if (address.fullName) currentStep = 1;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-4 pt-8">
        <Card className="w-full max-w-2xl bg-white rounded-2xl overflow-hidden">
          <div className="bg-slate-900 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <ShoppingCart className="w-5 h-5" />
              <h2 className="font-bold">{text.title}</h2>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-checkout">
              <X className="w-5 h-5" />
            </button>
          </div>

          <StepIndicator language={language} currentStep={currentStep} />

          <div className="p-6 space-y-6">
            <CartItemsList cartItems={cartItems} text={text} labels={labels} />

            {cartItems.length > 0 && (
              <AddressForm address={address} onChange={handleAddressChange} language={language} text={text} />
            )}

            {cartItems.length > 0 && (
              <OrderSummary
                text={text}
                language={language}
                subtotal={getSubtotal()}
                shippingFee={getShippingFee()}
                coupon={coupon}
                discount={getDiscount()}
                total={getTotal()}
                couponCode={couponCode}
                setCouponCode={setCouponCode}
                couponError={couponError}
                couponLoading={couponLoading}
                onApplyCoupon={applyCoupon}
                onRemoveCoupon={removeCoupon}
              />
            )}

            {cartItems.length > 0 && addressValid && !paymentMethod && (
              <PaymentMethodSelector
                language={language}
                text={text}
                itemCount={getItemCount()}
                mpProcessing={mpProcessing}
                onMercadoPago={handleMercadoPago}
                onSelectMethod={setPaymentMethod}
              />
            )}

            {/* PayPal — SDK loaded ONCE, buttons shown/hidden */}
            {paymentMethod === 'paypal' && paypalClientId && (
              <div className="space-y-3" data-testid="paypal-section">
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtonsWrapper
                    cartItems={cartItems}
                    total={getPreDiscountTotal()}
                    address={address}
                    couponCode={coupon?.code || null}
                    language={language}
                    onSuccess={handlePaypalSuccess}
                    onError={handlePaypalError}
                  />
                </PayPalScriptProvider>
                <Button variant="ghost" onClick={() => setPaymentMethod(null)} className="w-full text-slate-500 text-sm" data-testid="paypal-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> {text.back}
                </Button>
              </div>
            )}

            {paymentMethod === 'transfer' && (
              <TransferSection
                text={text}
                total={getTotal()}
                processing={transferProcessing}
                onSubmit={handleTransferSubmit}
                onBack={() => setPaymentMethod(null)}
              />
            )}

            {cartItems.length > 0 && !addressValid && (
              <p className="text-xs text-amber-600 text-center" data-testid="address-required-msg">{text.completeAddress}</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Checkout;
